'use client';

import React from 'react';
import { ActivityBarChart } from './ActivityBarChart';
import { PercentChart } from './PercentChart';
import { LineChart } from './LineChart';
import { 
  ActivityDataPoint, 
  SentimentCount, 
  CategoryCount, 
  PeriodType 
} from '../types'; // Assuming types are in ../types

interface ChartRendererProps {
  chartType: 'bar' | 'area' | 'percent' | 'line';
  activityData: ActivityDataPoint[];
  maxValue: number;
  currentPeriod: string;
  brandCategories: CategoryCount[]; // Renamed from brandData in ActivityGraph
  categoryData: CategoryCount[];
  sourceData: CategoryCount[];
  breakdownType: 'sentiment' | 'category' | 'brand' | 'source';
  categoriesByPeriod: { [period: string]: CategoryCount[] };
  brandsByPeriod: { [period: string]: CategoryCount[] };
  sourcesByPeriod: { [period: string]: CategoryCount[] };
  // Pass a single navigation handler
  onNavigate: (filterType: 'sentiment' | 'category' | 'brand' | 'source', filterValue: string) => void;
}

export function ChartRenderer({ 
  chartType, 
  activityData,
  maxValue,
  currentPeriod,
  brandCategories,
  categoryData,
  sourceData,
  breakdownType,
  categoriesByPeriod,
  brandsByPeriod,
  sourcesByPeriod,
  onNavigate
}: ChartRendererProps) {

  // Map the single onNavigate prop to individual click handlers
  const handleSentimentClick = (sentiment: string) => onNavigate('sentiment', sentiment);
  const handleCategoryClick = (category: string) => onNavigate('category', category);
  const handleBrandClick = (brand: string) => onNavigate('brand', brand);
  const handleSourceClick = (source: string) => onNavigate('source', source);

  return (
    <>
      {chartType === 'bar' && (
        <ActivityBarChart
          activityData={activityData}
          maxValue={maxValue}
          currentPeriod={currentPeriod}
          brandData={brandCategories} // Prop name mismatch? Check ActivityBarChart props
          categoryData={categoryData}
          sourceData={sourceData}
          breakdownType={breakdownType}
          categoriesByPeriod={categoriesByPeriod}
          brandsByPeriod={brandsByPeriod}
          sourcesByPeriod={sourcesByPeriod}
          onSentimentClick={handleSentimentClick}
          onCategoryClick={handleCategoryClick}
          onBrandClick={handleBrandClick}
          onSourceClick={handleSourceClick}
        />
      )}
      
      {chartType === 'area' && (
        <ActivityBarChart
          activityData={activityData}
          maxValue={maxValue}
          currentPeriod={currentPeriod}
          brandData={brandCategories} // Prop name mismatch? Check ActivityBarChart props
          categoryData={categoryData}
          sourceData={sourceData}
          breakdownType={breakdownType}
          categoriesByPeriod={categoriesByPeriod}
          brandsByPeriod={brandsByPeriod}
          sourcesByPeriod={sourcesByPeriod}
          onSentimentClick={handleSentimentClick}
          onCategoryClick={handleCategoryClick}
          onBrandClick={handleBrandClick}
          onSourceClick={handleSourceClick}
          chartType="area" // Pass area type
        />
      )}
      
      {chartType === 'percent' && (
        <PercentChart
          activityData={activityData}
          categoryData={categoryData}
          brandData={brandCategories} // Prop name mismatch? Check PercentChart props
          sourceData={sourceData}
          breakdownType={breakdownType}
          categoriesByPeriod={categoriesByPeriod}
          brandsByPeriod={brandsByPeriod}
          sourcesByPeriod={sourcesByPeriod}
          onSentimentClick={handleSentimentClick}
          onCategoryClick={handleCategoryClick}
          onBrandClick={handleBrandClick}
          onSourceClick={handleSourceClick}
        />
      )}
      
      {chartType === 'line' && (
        <LineChart
          activityData={activityData}
          maxValue={maxValue}
          categoryData={categoryData}
          brandData={brandCategories} // Prop name mismatch? Check LineChart props
          sourceData={sourceData}
          breakdownType={breakdownType}
          categoriesByPeriod={categoriesByPeriod}
          brandsByPeriod={brandsByPeriod}
          sourcesByPeriod={sourcesByPeriod}
          onSentimentClick={handleSentimentClick}
          onCategoryClick={handleCategoryClick}
          onBrandClick={handleBrandClick}
          onSourceClick={handleSourceClick}
        />
      )}
    </>
  );
} 